import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";

export interface EngagementProxy {
  UserId?: number;
  Name: string;
  Email: string;
  DelegateSendEmail: number | string;
  CreatorUserId: number | null;
  CreatorEmail: string | null;
  CreatorName: string | null;
}
export interface EngagementDelegation {
  EngagementId: string;
  EngagementName: string;
  EngagementStatus: string;
  EngagementOwnerUserId: number;
  EngagementOwnerName: string;
  EngagementOwnerEmail: string;
  IsAutoApprove: string;
  AutoApproveAmount: number;
  PartnerSendEmail: number | string;
  EngagementProxyDetails: EngagementProxy[];
}

export interface EngagementPreferenceDetails {
  EngagementPartnerNotification: boolean;
  AutoApprove: boolean;
  AutoApprovedAmount: number;
}

export interface DelegatePreferenceDetails {
  Name: string;
  Email: string;
  SendNotification: boolean;
  IsActive: boolean;
}

export interface EditEngagementPreferenceRequest {
  Action: "EditEngagementPreference";
  EngagementId: string;
  EngagementDetails: EngagementPreferenceDetails;
  DelegateDetails: DelegatePreferenceDetails[];
}

interface EngagementDelegationResponse {
  Status: string;
  Message?: string;
  Data?: EngagementDelegation[];
}

interface EngagementDelegationState {
  isLoading: boolean;
  data: EngagementDelegation[];
  totalRecords: number;
  error: string | null;
}

interface ResponseError {
  message: string;
}

const initialState: EngagementDelegationState = {
  isLoading: false,
  data: [],
  totalRecords: 0,
  error: null,
};

export const fetchEngagementDelegationList = createAsyncThunk<
  EngagementDelegationResponse,
  void,
  { rejectValue: ResponseError }
>(
  "EngagementDelegationSlice/fetchEngagementDelegationList",
  async (_, thunkApi) => {
    try {
      const response = await httpClient.post("/engagement/delegation", {
        Action: "list",
      });

      if (response.status !== 200 || response.data.Status !== "success") {
        return thunkApi.rejectWithValue({
          message:
            response.data.Message ||
            "Failed to fetch engagement delegation list",
        });
      }
      return response.data;
    } catch {
      return thunkApi.rejectWithValue({
        message: "Failed to fetch engagement delegation list",
      });
    }
  },
);

const EngagementDelegationSlice = createSlice({
  name: "EngagementDelegationSlice",
  initialState,
  reducers: {
    resetEngagementDelegationState: (state) => {
      state.isLoading = false;
      state.data = [];
      state.totalRecords = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEngagementDelegationList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchEngagementDelegationList.fulfilled,
        (state, action: PayloadAction<EngagementDelegationResponse>) => {
          state.isLoading = false;
          state.data = action.payload.Data || [];
          state.error = null;
        },
      )
      .addCase(fetchEngagementDelegationList.rejected, (state, action) => {
        state.isLoading = false;
        state.data = [];
        state.error =
          action.payload?.message ||
          "Failed to fetch engagement delegation list";
      });
  },
});

export const editEngagementDelegation = createAsyncThunk<
  any,
  EditEngagementPreferenceRequest
>(
  "EngagementDelegationSlice/editEngagementDelegation",
  async (payload, thunkApi) => {
    try {
      const response = await httpClient.post("/engagement/preference", payload);
      if (response.status !== 200 || response.data.Status !== "success") {
        return thunkApi.rejectWithValue({
          message: response.data.Message || "Failed to edit engagement delegation",
        });
      }
      return response.data;
    } catch (error: any) {
      if (error.status == 400) {
        return thunkApi.rejectWithValue({
          message:
            error.response.data.Message ||
            "Failed to edit engagement delegation",
        });
      }
      return thunkApi.rejectWithValue({
        message: error?.message || "Failed to edit engagement delegation",
      });
    }
  },
);

export const { resetEngagementDelegationState } =
  EngagementDelegationSlice.actions;

export default EngagementDelegationSlice.reducer;
